import axios, { AxiosResponse } from 'axios';
import { getRefreshToken, setTokens, clearTokens, getAccessToken } from '../utils/tokenUtils';

const API_BASE_URL = '/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

let refreshInFlight: Promise<{ accessToken: string; refreshToken?: string }> | null = null;

const refreshAccessToken = () => {
  if (refreshInFlight) return refreshInFlight;
  const refreshToken = getRefreshToken();
  if (!refreshToken) return Promise.reject(new Error('No refresh token available.'));

  refreshInFlight = axios.post(`${API_BASE_URL}/auth/refresh`, { refresh: refreshToken })
    .then(({ data }) => {
      const accessToken = String(data?.accessToken || '');
      const nextRefreshToken = String(data?.refreshToken || '');
      if (!accessToken || !nextRefreshToken) throw new Error('Invalid refresh response.');
      setTokens(accessToken, nextRefreshToken);
      return { accessToken, refreshToken: nextRefreshToken };
    })
    .finally(() => { refreshInFlight = null; });
  return refreshInFlight;
};

type DestinationHealthStatus = 'connecting' | 'connected' | 'disconnected' | 'disabled';

interface DestinationHealthMetrics {
  ip: string | null;
  trafficKbps: number;
  dataMoving: boolean;
  validIp: boolean;
  positiveTraffic: boolean;
  updatedAt?: string | null;
  source?: string;
}

interface DestinationHealthEnvelope {
  outputId: number;
  status: DestinationHealthStatus;
  metrics: DestinationHealthMetrics;
  raw?: any;
}

const isPullOutput = (output: any) => output?.deliveryMode === 'PULL' || output?.isPullLink === true;
const needsCanonicalHealth = (output: any) => output?.protocol === 'hls' || isPullOutput(output);

const resolveOutputRouteId = (output: any, outputId?: number) =>
  String(output?.transcoderHashId || output?.hashId || outputId || output?.id || '');

const fetchDestinationHealth = async (outputId: number, authorization?: string, output?: any) => {
  const headers = authorization ? { Authorization: authorization } : undefined;
  try {
    const routeId = resolveOutputRouteId(output, outputId);
    const { data } = await axios.get<DestinationHealthEnvelope>(`${API_BASE_URL}/streams/transcoders/${routeId}/health`, { headers });
    return data;
  } catch {
    return null;
  }
};

const normalizePullStatusEnvelope = (payload: any) => {
  const health = payload?.destinationHealth as DestinationHealthEnvelope | undefined;
  if (!payload?.status || !health) return payload;
  const connected = health.status === 'connected';
  return {
    ...payload,
    status: {
      ...payload.status,
      destinationHealth: health,
      status: connected ? 'running' : health.status === 'disabled' ? 'disabled' : 'idle',
      pulled: connected,
      connected,
      isConnected: connected,
      currentKbps: health.metrics?.trafficKbps || 0,
      dataMoving: health.metrics?.dataMoving === true,
      validIp: health.metrics?.validIp === true,
      positiveTraffic: health.metrics?.positiveTraffic === true,
      ip: health.metrics?.ip || null,
      lastActivityAt: health.metrics?.updatedAt || payload.status.lastActivityAt || null,
      currentViewer: health.metrics?.ip
        ? {
          ...(payload.status.currentViewer || {}),
          ip: health.metrics.ip,
          lastSeenAt: health.metrics.updatedAt || payload.status.currentViewer?.lastSeenAt || null,
        }
        : null,
    },
  };
};

const isPullStatusResponse = (response: AxiosResponse) => {
  const method = String(response.config?.method || 'get').toLowerCase();
  const url = String(response.config?.url || '');
  return method === 'get' && url.includes('/streams/transcoders/') && url.includes('/pull-status');
};

const isHlsStatusResponse = (response: AxiosResponse) => {
  const method = String(response.config?.method || 'get').toLowerCase();
  const url = String(response.config?.url || '');
  return method === 'get' && url.includes('/streams/transcoders/') && url.includes('/hls-session-status');
};

const isTranscodersListResponse = (response: AxiosResponse) => {
  const method = String(response.config?.method || 'get').toLowerCase();
  const url = String(response.config?.url || '');
  return method === 'get' && Array.isArray(response.data) && url.includes('/streams/channels/') && url.includes('/transcoders');
};

const normalizeOutputWithCanonicalHealth = async (output: any, authorization?: string) => {
  if (!output || !needsCanonicalHealth(output)) return output;

  const health = await fetchDestinationHealth(Number(output.id), authorization, output);
  if (!health) return output;

  const connected = health.status === 'connected';
  const disabled = health.status === 'disabled';
  const runtimeStatus = connected ? 'running' : disabled ? 'disabled' : 'idle';

  return {
    ...output,
    destinationHealth: health,
    pullStatus: isPullOutput(output)
      ? {
        ...(output.pullStatus || {}),
        destinationHealth: health,
        status: runtimeStatus,
        pulled: connected,
        connected,
        isConnected: connected,
        currentKbps: health.metrics.trafficKbps,
        dataMoving: health.metrics.dataMoving,
        validIp: health.metrics.validIp,
        positiveTraffic: health.metrics.positiveTraffic,
        ip: health.metrics.ip,
        viewerCount: health.metrics.ip ? 1 : 0,
        currentViewer: health.metrics.ip ? { ip: health.metrics.ip, lastSeenAt: health.metrics.updatedAt || null } : null,
        lastActivityAt: health.metrics.updatedAt || null,
      }
      : output.pullStatus,
    hlsSession: output.protocol === 'hls'
      ? {
        ...(output.hlsSession || {}),
        destinationHealth: health,
        clientIp: health.metrics.ip,
        averageKbps: health.metrics.trafficKbps,
        currentKbps: health.metrics.trafficKbps,
        dataMoving: health.metrics.dataMoving,
        validIp: health.metrics.validIp,
        positiveTraffic: health.metrics.positiveTraffic,
        lastSeenAt: health.metrics.updatedAt || null,
        status: connected ? 'ACTIVE' : 'DISCONNECTED',
      }
      : output.hlsSession,
    status: {
      ...(output.status || {}),
      status: runtimeStatus,
      stats: {
        ...(output.status?.stats || {}),
        destinationHealth: health,
        ip: health.metrics.ip,
        currentKbps: health.metrics.trafficKbps,
        averageKbps: health.metrics.trafficKbps,
        dataMoving: health.metrics.dataMoving,
        validIp: health.metrics.validIp,
        positiveTraffic: health.metrics.positiveTraffic,
        lastActivityAt: health.metrics.updatedAt || null,
      },
    },
  };
};

const normalizeTranscodersListResponse = async (response: AxiosResponse) => {
  if (!isTranscodersListResponse(response)) return response;
  const authorization = String((response.config?.headers as any)?.Authorization || '');
  const data = await Promise.all(response.data.map((output: any) => normalizeOutputWithCanonicalHealth(output, authorization)));
  return { ...response, data };
};

const normalizePullStatusResponse = async (response: AxiosResponse) => {
  if (!isPullStatusResponse(response)) return response;
  if (response.data?.destinationHealth || response.data?.status?.destinationHealth) return response;
  const url = String(response.config?.url || '');
  const match = url.match(/\/streams\/transcoders\/([^/]+)\/pull-status/);
  const outputId = Number(response.data?.outputId || 0) || (match && /^\d+$/.test(match[1]) ? Number(match[1]) : 0);
  if (!outputId) return response;
  const authorization = String((response.config?.headers as any)?.Authorization || '');
  const health = await fetchDestinationHealth(outputId, authorization);
  if (!health) return response;
  return { ...response, data: normalizePullStatusEnvelope({ ...response.data, destinationHealth: health }) };
};

const normalizeHlsStatusResponse = async (response: AxiosResponse) => {
  if (!isHlsStatusResponse(response)) return response;
  if (response.data?.destinationHealth || response.data?.activeSession?.destinationHealth) return response;
  const url = String(response.config?.url || '');
  const match = url.match(/\/streams\/transcoders\/([^/]+)\/hls-session-status/);
  const outputId = Number(response.data?.outputId || 0) || (match && /^\d+$/.test(match[1]) ? Number(match[1]) : 0);
  if (!outputId) return response;
  const authorization = String((response.config?.headers as any)?.Authorization || '');
  const health = await fetchDestinationHealth(outputId, authorization);
  if (!health) return response;
  const connected = health.status === 'connected';
  return {
    ...response,
    data: {
      ...response.data,
      destinationHealth: health,
      activeSession: health.status === 'disabled'
        ? null
        : {
          ...(response.data?.activeSession || {}),
          destinationHealth: health,
          clientIp: health.metrics.ip,
          averageKbps: health.metrics.trafficKbps,
          currentKbps: health.metrics.trafficKbps,
          dataMoving: health.metrics.dataMoving === true,
          validIp: health.metrics.validIp === true,
          positiveTraffic: health.metrics.positiveTraffic === true,
          lastSeenAt: health.metrics.updatedAt || null,
          status: connected ? 'ACTIVE' : 'DISCONNECTED',
        },
    },
  };
};

const normalizeResponse = async (response: AxiosResponse) => {
  if (isPullStatusResponse(response)) return normalizePullStatusResponse(response);
  if (isHlsStatusResponse(response)) return normalizeHlsStatusResponse(response);
  return normalizeTranscodersListResponse(response);
};

apiClient.interceptors.request.use(
  (config) => {
    const accessToken = getAccessToken();
    if (accessToken) config.headers['Authorization'] = `Bearer ${accessToken}`;
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  async (response) => normalizeResponse(response),
  async (error) => {
    const originalRequest = error.config;
    if (!error.response || !originalRequest) return Promise.reject(error);

    if (error.response.status === 401 && !originalRequest._retry && originalRequest.url !== '/users/verify-pin') {
      originalRequest._retry = true;

      if (originalRequest.url === '/auth/refresh') {
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const { accessToken: newAccessToken } = await refreshAccessToken();
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
